"use client"

import { useEffect, useMemo, useState, useRef, useLayoutEffect, useCallback } from "react"
import {
  MagnifyingGlass,
  Plus,
  FunnelSimple,
  DotsThreeOutlineVertical,
  PencilSimple,
  Trash,
  ArrowClockwise,
  X,
  WarningCircle,
  SpinnerGap,
  CalendarBlank,
  Globe,
  Eye,
  ArrowRight,
  CaretLeft,
  CaretRight,
  CaretDoubleLeft,
  CaretDoubleRight,
  PlusCircle,
  User,
  Buildings,
  Ghost,
  Check,
} from "phosphor-react"
import { createPortal } from "react-dom"
import { AnimatePresence, motion } from "framer-motion"

import { DateRangePicker, SearchableSelect, DeleteModal } from "../../components"
import ClientFormModal from "./ClientFormModal"
import ClientDetailsModal from "./ClientDetailsModal"
import clientsService from "../../services/clientsService"
import { useTranslation } from "react-i18next"
import { useAccessChecker } from "../../utils/Functions/Authorization"
import { useCurrentUser } from "../../utils/context/UserProvider"
import { countryOptions } from "../../utils/data/CountryDialCodes"
import { useImpersonation } from "../../utils/hooks/useImpersonation"

const Clients = () => {
  const { checkAccess } = useAccessChecker()
  const { currentUser } = useCurrentUser()
  const { t } = useTranslation()
  const { impersonate } = useImpersonation()

  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(false)
  const [anchorRect, setAnchorRect] = useState(null)

  // ✅ Top stats cards
  const [stats, setStats] = useState({
    totalClients: 0,
    totalActive: 0,
    totalInactive: 0,
  })

  // Search + filters
  const [searchQuery, setSearchQuery] = useState("")
  const [dateRange, setDateRange] = useState({ start: "", end: "" })
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  const [filters, setFilters] = useState({
    userType: "",
    status: "",
    country: "",
  })

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 5,
    totalPages: 1,
    totalItems: 0,
  })

  // Dropdown (Portal) ✅
  const [dropdownOpen, setDropdownOpen] = useState(null) // clientId
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 })
  const menuRef = useRef(null)
  const btnRefs = useRef(new Map()) // clientId -> button element

  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)

  const [clientFormOpen, setClientFormOpen] = useState(false)
  const [clientDetailsOpen, setClientDetailsOpen] = useState(false)
  const [selectedClientId, setSelectedClientId] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [done, setDone] = useState(false)

  // Shadow
  const [shadowingModal, setShadowingModal] = useState(false)
  const [shadowLoading, setShadowLoading] = useState(false)

  const filtersActive = useMemo(() => {
    return (
      searchQuery !== "" ||
      dateRange.start !== "" ||
      dateRange.end !== "" ||
      filters.userType !== "" ||
      filters.status !== "" ||
      filters.country !== ""
    )
  }, [searchQuery, dateRange, filters])

  /* ───────────────────────────── Title ───────────────────────────── */
  useEffect(() => {
    const event = new CustomEvent("setPageTitle", {
      detail: { title: t("Client Management") },
    })
    window.dispatchEvent(event)

    return () => {
      const resetEvent = new CustomEvent("setPageTitle", {
        detail: { title: t("Dashboard") },
      })
      window.dispatchEvent(resetEvent)
    }
  }, [t])

  /* ───────────────────────────── Fetch ───────────────────────────── */
  const fetchClients = useCallback(
    async (overrides = {}) => {
      try {
        setLoading(true)

        const page = overrides.page ?? pagination.currentPage
        const limit = overrides.limit ?? pagination.pageSize

        const payload = {
          ...filters,
          searchValue: searchQuery,
          dateRange,
          pagination: { page, limit },
        }

        const response = await clientsService.search(payload)
        const responseData = response.data

        if (responseData?.statusCode === 200) {
          const clientUsers = (responseData.data || []).filter((u) =>
            ["advertiser", "agency", "partner", "reseller", "affiliate"].includes(u.userType)
          )

          setClients(clientUsers)

          if (responseData.pagination) {
            setPagination((p) => ({
              ...p,
              currentPage: responseData.pagination.currentPage,
              pageSize: responseData.pagination.pageSize,
              totalPages: responseData.pagination.totalPages,
              totalItems: responseData.pagination.totalItems,
            }))
          }

          const totals = responseData.totals || {}

          const totalClients =
            Number(totals.totalClients) ||
            Number(responseData.pagination?.totalItems) ||
            0

          const totalActive = Number(totals.totalActive) || 0

          const totalInactive =
            Number(totals.totalInactive) ||
            Math.max(0, totalClients - totalActive)

          setStats({
            totalClients,
            totalActive,
            totalInactive,
          })
        }
      } catch (error) {
        console.error("Failed to fetch clients:", error)
      } finally {
        setLoading(false)
      }
    },
    [filters, searchQuery, dateRange, pagination.currentPage, pagination.pageSize]
  )

  useEffect(() => {
    fetchClients()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, pagination.currentPage, pagination.pageSize])

  /* ───────────────────────────── Helpers ───────────────────────────── */
  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }))
  }

  const handleDateRangeChange = (range) => {
    setDateRange(range)
  }

  const clearFilters = () => {
    setFilters({ userType: "", status: "", country: "" })
    setSearchQuery("")
    setDateRange({ start: "", end: "" })
  }

  const applyFilters = () => {
    setPagination((p) => ({ ...p, currentPage: 1 }))
    fetchClients({ page: 1 })
  }

  const resetAndRefresh = () => {
    clearFilters()
    setPagination((p) => ({ ...p, currentPage: 1 }))
    setTimeout(() => fetchClients({ page: 1 }), 0)
  }

  const openClientForm = (id = null, edit = false) => {
    setSelectedClientId(id)
    setIsEditing(edit)
    setClientFormOpen(true)
    setDropdownOpen(null)
  }

  const openClientDetails = (id) => {
    setSelectedClientId(id)
    setClientDetailsOpen(true)
    setDropdownOpen(null)
  }

  const handleFormSuccess = () => setDone((d) => !d)

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return
    setPagination((p) => ({ ...p, currentPage: newPage }))
  }

  const handlePageSizeChange = (e) => {
    const newSize = Number.parseInt(e.target.value)
    setPagination((p) => ({ ...p, pageSize: newSize, currentPage: 1 }))
    setTimeout(() => fetchClients({ page: 1, limit: newSize }), 0)
  }

  const handleSearch = () => {
    setPagination((p) => ({ ...p, currentPage: 1 }))
    fetchClients({ page: 1 })
  }

const formatDate = (raw) => {
    if (!raw) return "N/A"
    const dateString = String(raw).trim()
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    const dotDateRE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/
    const hasExplicitTime =
      /T\d{2}:\d{2}/.test(dateString) || /\s\d{1,2}:\d{2}/.test(dateString)

    let d
    const m = dateString.match(dotDateRE)
    if (m) {
      const [, dd, mm, yyyy, HH, MM] = m
      d = new Date(
        parseInt(yyyy, 10),
        parseInt(mm, 10) - 1,
        parseInt(dd, 10),
        HH ? parseInt(HH, 10) : 0,
        MM ? parseInt(MM, 10) : 0,
        0,
        0
      )
    } else {
      d = new Date(dateString)
    }
    if (isNaN(d.getTime())) return raw

    const datePart = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone,
    }).format(d)

    if (!hasExplicitTime) return datePart

    const timeFormatter = new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone,
    })

    const parts = timeFormatter.formatToParts(d)
    const hour = parts.find((p) => p.type === "hour")?.value ?? "00"
    const minute = parts.find((p) => p.type === "minute")?.value ?? "00"
    const hourNum = parseInt(hour, 10)
    const suffix = hourNum < 12 ? "AM" : "PM"
    const timePart = `${hour}:${minute} ${suffix}`

    return `${datePart}, ${timePart}`
  }

const formatDateTime = (raw) => {
    if (!raw) return "N/A"
    const d = new Date(raw)
    if (isNaN(d.getTime())) return raw
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const datePart = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone,
    }).format(d)
    const timeFormatter = new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone,
    })
    const parts = timeFormatter.formatToParts(d)
    const hour = parts.find((p) => p.type === "hour")?.value ?? "00"
    const minute = parts.find((p) => p.type === "minute")?.value ?? "00"
    const suffix = parseInt(hour, 10) < 12 ? "AM" : "PM"
    return `${datePart}, ${hour}:${minute} ${suffix}`
  }
  const getCountryFlag = (countryName) => {
    const country = countryOptions.find((c) => c.name === countryName)
    return country ? country.flag : null
  }

  const normalizeStatus = (status) => {
    const s = String(status || "").trim()
    if (s === "inActive") return "inactive"
    return s.toLowerCase()
  }

  const formatStatusLabel = (status) => {
    const ns = normalizeStatus(status)
    if (!ns) return t("Unknown")
    if (ns === "inactive") return t("Inactive")
    return ns.charAt(0).toUpperCase() + ns.slice(1)
  }

  const getStatusBadgeColor = (status) => {
    const ns = normalizeStatus(status)
    switch (ns) {
      case "active":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
      case "inactive":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
      case "frozen":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
      case "suspended":
        return "bg-white text-red-700 border border-red-300 dark:bg-neutral-900 dark:text-red-400 dark:border-red-500"
      case "blacklisted":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
      default:
        return "bg-neutral-100 text-neutral-800 dark:bg-neutral-900/30 dark:text-neutral-400"
    }
  }

  const getStatusDot = (status) => {
    const ns = normalizeStatus(status)
    if (ns === "active") return "bg-green-500"
    if (ns === "inactive") return "bg-red-500"
    if (ns === "pending") return "bg-yellow-500"
    if (ns === "blacklisted") return "bg-red-600"
    if (ns === "suspended") return "bg-red-500"
    if (ns === "frozen") return "bg-orange-500"
    return "bg-orange-500"
  }

  const getClientTypeDisplay = (type) => {
    switch (type) {
      case "advertiser":
        return t("Advertiser")
      case "agency":
        return t("Agency")
      case "partner":
        return t("Partner")
      case "reseller":
        return t("Reseller")
      case "affiliate":
        return t("Affiliate")
      default:
        return t("Unknown")
    }
  }

  /* ───────────────────────────── Filter Options ───────────────────────────── */
  const clientTypeOptions = [
    { id: 1, name: t("All Types"), value: "" },
    { id: 2, name: t("Advertiser"), value: "advertiser" },
    { id: 3, name: t("Agency"), value: "agency" },
    { id: 4, name: t("Partner"), value: "partner" },
    { id: 5, name: t("Reseller"), value: "reseller" },
    { id: 6, name: t("Affiliate"), value: "affiliate" },
  ]

  const statusOptions = [
    { id: 1, name: t("All Statuses"), value: "" },
    { id: 2, name: t("Active"), value: "active" },
    { id: 3, name: t("Inactive"), value: "inactive" },
    { id: 4, name: t("Pending"), value: "pending" },
    { id: 5, name: t("Suspended"), value: "suspended" },
    { id: 6, name: t("Frozen"), value: "frozen" },
    { id: 7, name: t("Blacklisted"), value: "blacklisted" },
  ]

  /* ───────────────────────────── Dropdown (Portal) ───────────────────────────── */
  const closeDropdown = useCallback(() => setDropdownOpen(null), [])

  const openDropdown = useCallback((clientId) => {
    const btn = btnRefs.current.get(clientId)
    if (!btn) return

    const r = btn.getBoundingClientRect()
    setAnchorRect(r)

    const menuW = 192 // w-48
    const gap = 8
    const pad = 8

    let left = r.right - menuW
    if (left < pad) left = r.left
    left = Math.min(Math.max(left, pad), window.innerWidth - menuW - pad)

    const top = r.bottom + gap

    setDropdownPos({ top, left })
    setDropdownOpen(clientId)
  }, [])

  const toggleDropdown = useCallback(
    (clientId) => {
      if (dropdownOpen === clientId) return closeDropdown()
      openDropdown(clientId)
    },
    [dropdownOpen, closeDropdown, openDropdown]
  )

  useLayoutEffect(() => {
    if (!dropdownOpen || !menuRef.current || !anchorRect) return

    const menu = menuRef.current
    const pad = 8
    const gap = 8
    const mh = menu.getBoundingClientRect().height

    let top = anchorRect.bottom + gap
    if (top + mh > window.innerHeight - pad) {
      top = Math.max(pad, anchorRect.top - mh - gap)
    }

    if (Math.abs(top - dropdownPos.top) > 1) {
      setDropdownPos((p) => ({ ...p, top }))
    }
  }, [dropdownOpen, anchorRect, dropdownPos.top])

  useEffect(() => {
    if (!dropdownOpen) return

    const onMouseDown = (e) => {
      const menu = menuRef.current
      const btn = btnRefs.current.get(dropdownOpen)

      if (menu && menu.contains(e.target)) return
      if (btn && btn.contains(e.target)) return
      closeDropdown()
    }

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeDropdown()
    }

    const onAnyScroll = () => closeDropdown()

    window.addEventListener("mousedown", onMouseDown)
    window.addEventListener("keydown", onKeyDown)
    window.addEventListener("scroll", onAnyScroll, true)
    window.addEventListener("resize", onAnyScroll)

    return () => {
      window.removeEventListener("mousedown", onMouseDown)
      window.removeEventListener("keydown", onKeyDown)
      window.removeEventListener("scroll", onAnyScroll, true)
      window.removeEventListener("resize", onAnyScroll)
    }
  }, [dropdownOpen, closeDropdown])

  /* ───────────────────────────── Shadow (FIX) ───────────────────────────── */
  const openShadowConfirm = (clientId) => {
    setSelectedClientId(clientId)
    setShadowingModal(true)
  }

  const handleStartShadow = async () => {
    if (!selectedClientId || shadowLoading) return
    try {
      setShadowLoading(true)
      await impersonate(selectedClientId) // ✅ pass the client id
      setShadowingModal(false)
    } catch (e) {
      console.error(e)
      // startShadow should toast; we keep UI same
    } finally {
      setShadowLoading(false)
    }
  }

  /* ───────────────────────────── Animation ───────────────────────────── */
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } },
  }

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 10, transition: { duration: 0.15 } },
    visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.2, delay: 0.05 } },
    exit: { opacity: 0, scale: 0.95, transition: { duration: 0.15 } },
  }

  const buttonVariants = {
    hover: { scale: 1.03 },
    tap: { scale: 0.97 },
  }

  /* ───────────────────────────── Render ───────────────────────────── */
  return (
    <div className="lg:p-7 p-3 max-w-full mx-auto">
      {/* ✅ Top Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
        <StatCard
          title={t("Total Clients")}
          value={stats.totalClients}
          icon={<Buildings className="text-yellow-700" size={18} />}
          color="neutral"
        />
        <StatCard
          title={t("Active Clients")}
          value={stats.totalActive}
          icon={<Check className="text-green-700" size={18} />}
          color="green"
        />
        <StatCard
          title={t("Inactive Clients")}
          value={stats.totalInactive}
          icon={<WarningCircle className="text-red-700" size={18} />}
          color="red"
        />
      </div>

      {/* Search and filters top bar */}
      <div className="flex flex-wrap gap-3 mb-6">
        {/* Search input */}
        <div className="relative flex-grow max-w-xs">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <MagnifyingGlass size={16} className="text-neutral-800 dark:text-neutral-400" />
          </div>
          <input
            type="text"
            placeholder={t("Search clients...")}
            className="h-10 w-full border pl-10 pr-10 text-sm border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl placeholder-neutral-500 cursor-pointer ring-neutral-300 dark:ring-neutral-700 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200 focus:outline-none focus:ring-1"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
            onClick={handleSearch}
            aria-label={t("Submit search")}
          >
            <ArrowRight size={16} />
          </button>
        </div>

        {/* Date range picker */}
        <DateRangePicker
          onRangeChange={handleDateRangeChange}
          value={dateRange}
          className={
            dateRange.start || dateRange.end
              ? "border-blue-500 bg-blue-50 text-blue-600 dark:border-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
              : ""
          }
        />

        {/* More filters */}
        <button
          className={`flex items-center ring-yellow-500/50 gap-2 px-4 py-2.5 h-10 w-fit border text-sm rounded-xl cursor-pointer focus:outline-none focus:ring-1 ${showMoreFilters
              ? "border-yellow-500/50 bg-yellow-50 text-yellow-600 dark:border-yellow-600/30 dark:bg-yellow-900/20 dark:text-yellow-400"
              : "border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200"
            }`}
          onClick={() => setShowMoreFilters(!showMoreFilters)}
        >
          <span className="text-sm">{showMoreFilters ? t("Hide filters") : t("More filters")}</span>
          {showMoreFilters ? (
            <X size={16} className="text-yellow-600 dark:text-yellow-400" />
          ) : (
            <PlusCircle size={16} className="text-neutral-800 dark:text-neutral-400" />
          )}
        </button>

        {/* Reset */}
        <button
          className="flex items-center gap-2 h-10 px-4 border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl bg-white dark:bg-neutral-950 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-800"
          onClick={resetAndRefresh}
          aria-label="Reset filters"
        >
          <ArrowClockwise size={16} className="text-neutral-800 dark:text-neutral-400" />
          <span className="text-sm">{t("Reset")}</span>
        </button>

        {/* Apply filters button */}
        {filtersActive && !showMoreFilters && (
          <button
            className="flex items-center text-xs justify-center px-4 gap-2 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400"
            onClick={applyFilters}
          >
            <FunnelSimple size={16} />
            <span className="text-xs">{t("Apply Filters")}</span>
          </button>
        )}

        {/* Create button */}
        {checkAccess("clients", "create") && (
          <button
            className="flex items-center gap-2 px-4 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400 ml-auto"
            onClick={() => openClientForm()}
          >
            <Plus size={16} />
            <span className="text-sm">{t("Add Client")}</span>
          </button>
        )}
      </div>

      {/* Active Filters Chips */}
      {filtersActive && (
        <div className="mb-4 flex flex-wrap gap-2 items-center">
          <span className="text-sm text-neutral-500 dark:text-neutral-400">{t("Active filters")}:</span>

          {searchQuery && (
            <Chip
              icon={<MagnifyingGlass size={12} />}
              label={searchQuery}
              onClear={() => {
                setSearchQuery("")
                handleSearch()
              }}
            />
          )}

          {(dateRange.start || dateRange.end) && (
            <Chip
              icon={<CalendarBlank size={12} />}
              label={`${dateRange.start ? new Date(dateRange.start).toLocaleDateString() : t("Any")} - ${dateRange.end ? new Date(dateRange.end).toLocaleDateString() : t("Any")
                }`}
              onClear={() => {
                setDateRange({ start: "", end: "" })
                handleSearch()
              }}
            />
          )}

          {filters.userType && (
            <Chip
              icon={<User size={12} />}
              label={getClientTypeDisplay(filters.userType)}
              onClear={() => {
                handleFilterChange("userType", "")
                applyFilters()
              }}
            />
          )}

          {filters.status && (
            <Chip
              icon={<WarningCircle size={12} />}
              label={formatStatusLabel(filters.status)}
              onClear={() => {
                handleFilterChange("status", "")
                applyFilters()
              }}
            />
          )}

          {filters.country && (
            <Chip
              icon={<Globe size={12} />}
              label={filters.country}
              onClear={() => {
                handleFilterChange("country", "")
                applyFilters()
              }}
            />
          )}

          <button
            onClick={resetAndRefresh}
            className="text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1 ml-2"
          >
            <X size={12} />
            <span>{t("Clear all")}</span>
          </button>
        </div>
      )}

      {/* Expanded filters section */}
      {showMoreFilters && (
        <div className="mb-6 p-6 border border-neutral-150 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950/80">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-[300] text-neutral-800 dark:text-white">{t("More filters")}</h2>
            <button
              onClick={clearFilters}
              className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1.5"
            >
              <X size={14} />
              <span>{t("Clear all")}</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <SearchableSelect
              options={clientTypeOptions}
              value={filters.userType}
              onChange={(value) => handleFilterChange("userType", value)}
              placeholder={t("Client Type")}
              icon={<User size={16} />}
            />

            <SearchableSelect
              options={statusOptions}
              value={filters.status}
              onChange={(value) => handleFilterChange("status", value)}
              placeholder={t("Status")}
              icon={<WarningCircle size={16} />}
            />

            <SearchableSelect
              options={countryOptions}
              value={filters.country}
              onChange={(value) => handleFilterChange("country", value)}
              placeholder={t("Country")}
              icon={<Globe size={16} />}
            />

            <div className="flex items-center gap-2">
              <button
                className="flex items-center justify-center px-4 gap-2 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400"
                onClick={applyFilters}
              >
                <FunnelSimple size={16} />
                <span className="text-sm">{t("Apply Filters")}</span>
              </button>

              <button
                className="flex items-center justify-center px-4 gap-2 h-10 border border-neutral-200 dark:bg-neutral-800 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700"
                onClick={resetAndRefresh}
              >
                <ArrowClockwise size={16} />
                <span className="text-sm">{t("Reset")}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clients Table */}
      <div className="border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-lg bg-white">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <SpinnerGap
              size={30}
              className="animate-[spin_0.8s_linear_infinite] text-neutral-600 dark:text-neutral-400 drop-shadow-sm"
            />
          </div>
        ) : clients.length > 0 ? (
          <>
            <div className="overflow-x-auto rounded-t-lg">
              <table className="min-w-full whitespace-nowrap divide-y divide-neutral-150 dark:divide-neutral-800">
                <thead className="bg-neutral-50 text-start h-14 dark:bg-neutral-900/30">
                  <tr>
                    <Th>{t("Onboarded On")}</Th>
                    <Th>{t("Client ID")}</Th>
                    <Th>{t("Client Reference")}</Th>
                    <Th>{t("Business Info")}</Th>
                    <Th>{t("Client Type")}</Th>
                    <Th>{t("Category")}</Th>
                    <Th>{t("Status")}</Th>
                    <Th>{t("Actions")}</Th>
                  </tr>
                </thead>

                <tbody className="bg-white font-[300] dark:bg-neutral-950 divide-y divide-neutral-150 dark:divide-neutral-800">
                  {clients.map((client) => (
                    <tr
                      key={client._id}
                      className="hover:bg-neutral-50 text-start dark:hover:bg-neutral-800/50"
                    >
                      <Td className="text-neutral-800 dark:text-white">{formatDateTime(client.joinDate || client.createdAt)}</Td>
                      <Td className="text-neutral-800 dark:text-white">{client.clientId || "-None-"}</Td>

                      <Td>
                        <div className="text-sm font-[300] text-neutral-800 dark:text-white">
                          {client.reference || client.fullName}
                        </div>
                      </Td>

                      <Td className="space-y-1">
                        <div className="text-sm font-[300] text-neutral-800 dark:text-white">
                          {client.businessName || t("Not specified")}
                        </div>

                        {client.email && (
                          <div className="text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-1">
                            <Globe size={12} />
                            <span>{client.email}</span>
                          </div>
                        )}

                        {client.country && (
                          <div className="text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-2">
                            {getCountryFlag(client.country) ? (
                              <img
                                src={getCountryFlag(client.country)}
                                className="h-2.5 w-4 object-cover"
                                alt={client.country}
                              />
                            ) : null}
                            <span>{client.country}</span>
                          </div>
                        )}
                      </Td>

                      <Td className="text-neutral-800 dark:text-white">{getClientTypeDisplay(client.userType)}</Td>
                      <Td className="text-neutral-800 dark:text-white">{client.category || t("Not specified")}</Td>

                      <Td>
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-[300] ${getStatusBadgeColor(
                            client.status
                          )}`}
                        >
                          <span className={`w-2 h-2 mr-1.5 rounded-full ${getStatusDot(client.status)}`} />
                          {formatStatusLabel(client.status)}
                        </span>
                      </Td>

                      <Td className="client-dropdown">
                        <button
                          ref={(node) => {
                            if (!node) btnRefs.current.delete(client._id)
                            else btnRefs.current.set(client._id, node)
                          }}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleDropdown(client._id)
                          }}
                          className="p-1.5 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400"
                        >
                          <DotsThreeOutlineVertical size={16} weight="bold" />
                        </button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30">
              <div className="flex items-center mb-4 sm:mb-0">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  {t("Showing")} {clients.length} {t("of")} {pagination.totalItems} {t("clients")}
                </span>

                <div className="ml-4">
                  <select
                    value={pagination.pageSize}
                    onChange={handlePageSizeChange}
                    className="h-8 px-2 text-xs border border-neutral-200 dark:bg-neutral-800 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-white"
                  >
                    <option value={5}>5 / page</option>
                    <option value={10}>10 {t("per page")}</option>
                    <option value={25}>25 {t("per page")}</option>
                    <option value={50}>50 {t("per page")}</option>
                    <option value={100}>100 {t("per page")}</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <PageBtn disabled={pagination.currentPage === 1} onClick={() => handlePageChange(1)} aria={t("First page")}>
                  <CaretDoubleLeft size={16} />
                </PageBtn>

                <PageBtn
                  disabled={pagination.currentPage === 1}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  aria={t("Previous page")}
                >
                  <CaretLeft size={16} />
                </PageBtn>

                <div className="flex items-center space-x-1">
                  {getPageNumbers(pagination.currentPage, pagination.totalPages).map((page, index) =>
                    page === "..." ? (
                      <span key={`ellipsis-${index}`} className="px-2 py-1 text-neutral-500 dark:text-neutral-400">
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-8 h-8 flex items-center justify-center rounded-xl text-sm ${pagination.currentPage === page
                            ? "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black"
                            : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                          }`}
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>

                <PageBtn
                  disabled={pagination.currentPage === pagination.totalPages || pagination.totalPages === 0}
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  aria={t("Next page")}
                >
                  <CaretRight size={16} />
                </PageBtn>

                <PageBtn
                  disabled={pagination.currentPage === pagination.totalPages || pagination.totalPages === 0}
                  onClick={() => handlePageChange(pagination.totalPages)}
                  aria={t("Last page")}
                >
                  <CaretDoubleRight size={16} />
                </PageBtn>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col text-sm items-center justify-center h-64 p-8">
            <div className="p-2 rounded-full bg-neutral-100 dark:bg-neutral-900/20 flex items-center justify-center mb-4">
              <WarningCircle size={24} className="text-neutral-600 dark:text-neutral-400" />
            </div>

            <h3 className="text-xl font-medium text-neutral-900 dark:text-white mb-2">{t("No Clients Found")}</h3>

            <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center max-w-md mb-6">
              {searchQuery || Object.values(filters).some(Boolean) || dateRange.start || dateRange.end
                ? t("No clients match your search criteria. Try adjusting your filters or search term.")
                : t("There are no clients in the system yet. Add your first client to get started.")}
            </p>

            <div className="flex gap-4">
              <button
                onClick={resetAndRefresh}
                className="px-4 py-2 border border-neutral-200 dark:bg-neutral-800 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors flex items-center gap-2"
              >
                <ArrowClockwise size={16} />
                {t("Reset Filters")}
              </button>

              {checkAccess("clients", "create") && (
                <button
                  onClick={() => openClientForm()}
                  className="px-4 py-2 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400 flex items-center gap-2"
                >
                  <Plus size={16} />
                  <span>{t("Add Client")}</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ✅ Dropdown Portal ONCE (NOT inside rows) */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {dropdownOpen && (
              <motion.div
                ref={menuRef}
                className="fixed w-48 bg-white dark:bg-neutral-900/70 backdrop-blur-md border border-neutral-200 dark:border-neutral-800/30 rounded-xl shadow-lg z-[9999] p-1"
                style={{ top: dropdownPos.top, left: dropdownPos.left }}
                initial={{ opacity: 0, scale: 0.98, y: 4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98, y: 4 }}
                transition={{ duration: 0.12 }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    openClientDetails(dropdownOpen)
                    closeDropdown()
                  }}
                  className="flex items-center w-full text-left px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-xl"
                >
                  <Eye size={16} className="mr-2" />
                  {t("View Details")}
                </button>

                {checkAccess("clients", "update") && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      openClientForm(dropdownOpen, true)
                      closeDropdown()
                    }}
                    className="flex items-center w-full text-left px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-xl"
                  >
                    <PencilSimple size={16} className="mr-2" />
                    {t("Edit Client")}
                  </button>
                )}

                {currentUser?.role?.name === "SuperAdmin" && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      openShadowConfirm(dropdownOpen) // ✅ sets selectedClientId + opens modal
                      closeDropdown()
                    }}
                    className="flex items-center w-full text-left px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-xl"
                  >
                    <Ghost size={16} className="mr-2" />
                    {t("Shadow Client")}
                  </button>
                )}

                {checkAccess("clients", "delete") && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setDeleteId(dropdownOpen)
                      setDeleteModal(true)
                      closeDropdown()
                    }}
                    className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-xl"
                  >
                    <Trash size={16} className="mr-2" />
                    {t("Delete")}
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}

      {/* Shadowing modal */}
      <AnimatePresence>
        {shadowingModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            <motion.div
              className="w-full max-w-md rounded-xl shadow-lg bg-white dark:bg-neutral-950/70 backdrop-blur-md text-neutral-800 dark:text-white overflow-hidden border border-neutral-200 dark:border-neutral-800/30"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-150 dark:border-neutral-800/30">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-full text-neutral-600 dark:text-neutral-400">
                    <Ghost className="w-5 h-5 text-orange-600" />
                  </div>
                  <h3 className="text-md font-[400]">{t("Start Shadowing Client")}</h3>
                </div>

                <motion.button
                  onClick={() => {
                    if (!shadowLoading) setShadowingModal(false)
                  }}
                  className="p-1 rounded-full text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors"
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  disabled={shadowLoading}
                >
                  <X size={18} />
                </motion.button>
              </div>

              <div className="px-6 py-4">
                <p className="text-neutral-600 font-[300] text-sm dark:text-neutral-300 mb-3">
                  {t("Are you sure you want to start shadowing this client?")}
                </p>
                <p className="text-neutral-500 font-[300] text-xs dark:text-neutral-400">
                  {t(
                    "You will be logged in as this client and can access their account. You can stop shadowing at any time from the navbar."
                  )}
                </p>
              </div>

              <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-150 dark:border-neutral-800/30">
                <motion.button
                  onClick={() => setShadowingModal(false)}
                  disabled={shadowLoading}
                  className={`px-4 py-1.5 text-sm font-medium rounded-xl bg-white dark:bg-neutral-800/10 text-neutral-700 dark:text-white hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors border border-neutral-300 dark:border-neutral-800/30 ${shadowLoading ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  variants={buttonVariants}
                  whileHover={!shadowLoading ? "hover" : undefined}
                  whileTap={!shadowLoading ? "tap" : undefined}
                >
                  {t("Cancel")}
                </motion.button>

                <motion.button
                  onClick={handleStartShadow} // ✅ fixed
                  disabled={shadowLoading}
                  className={`px-4 py-1.5 text-sm font-medium text-white rounded-xl transition-colors flex items-center bg-orange-600 hover:bg-orange-700 ${shadowLoading ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  variants={buttonVariants}
                  whileHover={!shadowLoading ? "hover" : undefined}
                  whileTap={!shadowLoading ? "tap" : undefined}
                >
                  <Ghost size={16} className="mr-2" />
                  {shadowLoading ? t("Starting...") : t("Start Shadowing")}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        setDeleteId={setDeleteId}
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={deleteId}
        Model="clients"
        setDone={setDone}
        setLoding={setLoading}
        done={done}
      />

      {/* Client Form Modal (Add/Edit) */}
      <ClientFormModal
        isOpen={clientFormOpen}
        onClose={() => setClientFormOpen(false)}
        clientId={selectedClientId}
        isEditing={isEditing}
        onSuccess={handleFormSuccess}
      />

      {/* Client Details Modal */}
      <ClientDetailsModal
        isOpen={clientDetailsOpen}
        onClose={() => setClientDetailsOpen(false)}
        clientId={selectedClientId}
        onEdit={(id) => {
          setClientDetailsOpen(false)
          openClientForm(id, true)
        }}
      />
    </div>
  )
}

export default Clients

/* ───────────────────────────── Small UI Components ───────────────────────────── */

function Th({ children }) {
  return (
    <th className="px-6 py-3 text-start text-xs font-[300] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
      {children}
    </th>
  )
}

function Td({ children, className = "" }) {
  return (
    <td className={`px-6 py-3 whitespace-nowrap text-sm font-[300] text-neutral-800 dark:text-white ${className}`}>
      {children}
    </td>
  )
}

function Chip({ icon, label, onClear }) {
  return (
    <div className="flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-full text-xs">
      {icon}
      <span>{label}</span>
      <button onClick={onClear} className="ml-1 hover:text-blue-900 dark:hover:text-blue-100">
        <X size={12} />
      </button>
    </div>
  )
}

function PageBtn({ disabled, onClick, children, aria }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`p-1.5 rounded-xl ${disabled
          ? "text-neutral-400 dark:text-neutral-600 cursor-not-allowed"
          : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
        }`}
      aria-label={aria}
    >
      {children}
    </button>
  )
}

function getPageNumbers(currentPage, totalPages) {
  if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1)
  if (currentPage <= 3) return [1, 2, 3, 4, "...", totalPages]
  if (currentPage >= totalPages - 2) return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages]
}

/* ✅ Tailwind-safe colors (no dynamic class strings) */
const STAT_COLOR = {
  yellow: { text: "text-yellow-600 dark:text-yellow-600", bg: "bg-yellow-100 dark:bg-yellow-900/30" },
  green: { text: "text-green-600 dark:text-green-600", bg: "bg-green-100 dark:bg-green-900/30" },
  red: { text: "text-red-600 dark:text-red-600", bg: "bg-red-100 dark:bg-red-900/30" },
  neutral: { text: "text-neutral-900 dark:text-neutral-200", bg: "bg-yellow-100 dark:bg-yellow-900/30" },
}

function StatCard({ title, value, icon, color }) {
  const c = STAT_COLOR[color] || STAT_COLOR.yellow
  return (
    <div className="border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 rounded-xl p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-start justify-center flex-col">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">{title}</p>
          <p className={`text-2xl font-normal ${c.text}`}>{Number(value || 0).toLocaleString("en-US")}</p>
        </div>

        <div className={`p-2 ${c.bg} rounded-xl`}>{icon}</div>
      </div>
    </div>
  )
}